import { FormikErrors } from 'formik';
import { getTrans } from 'modules/i18n';
import { CVBuilderFormKeys, CVBuilderFormValues } from 'utils/cv-form-db';
import { isRequired, validateCVForm } from '../../utils';

export const validatePersonalDetails = (
  personalDetails: CVBuilderFormValues[CVBuilderFormKeys.PERSONAL_DETAILS]
) => {
  const errors: FormikErrors<
    CVBuilderFormValues[CVBuilderFormKeys.PERSONAL_DETAILS]
  > = {};

  const fieldsToValidate = [
    {
      field: 'firstname',
      message: getTrans(
        'cvBuilderPage.personalDetails.form.firstName.required.label'
      )
    },
    {
      field: 'lastname',
      message: getTrans(
        'cvBuilderPage.personalDetails.form.lastName.required.label'
      )
    },
    {
      field: 'email',
      message: getTrans(
        'cvBuilderPage.personalDetails.form.email.required.label'
      )
    },
    {
      field: 'phone',
      message: getTrans(
        'cvBuilderPage.personalDetails.form.phoneNumber.required.label'
      )
    },
    {
      field: 'zip',
      message: getTrans(
        'cvBuilderPage.personalDetails.form.zipCode.required.label'
      )
    },
    {
      field: 'weeklyAvailableHours',
      message: getTrans(
        'cvBuilderPage.personalDetails.form.hoursPerWeek.required.label'
      )
    },
    {
      field: 'stipendBafoeg',
      message: getTrans(
        'cvBuilderPage.personalDetails.form.bafoeg.required.label'
      )
    },
    {
      field: 'insuranceName',
      message: getTrans(
        'cvBuilderPage.personalDetails.form.insuranceName.required.label'
      )
    },
    {
      field: 'insuranceType',
      message: getTrans(
        'cvBuilderPage.personalDetails.form.insuranceType.required.label'
      )
    }
  ] as const;

  fieldsToValidate.forEach(({ field, message }) => {
    if (isRequired(personalDetails[field])) {
      errors[field] = message;
    }
  });

  return errors;
};

/**
 * Single source of truth for "are the personal-details fields complete and valid?":
 * every required field is filled AND `validateCVForm` reports no errors on the
 * section. Used by the step-navigation gate (`useStepValidation`, "can I advance
 * now?") and the progress layer (`useProgressCalculation`, which additionally
 * requires the step to be acknowledged in `completedSteps`). Keep this the only
 * implementation so the two layers can never drift again.
 *
 * Deliberately no `value !== 0` clause: personalDetails values are strings ('' when
 * empty), and `validateCVForm`/`isRequired` already rejects a missing/zero-ish value,
 * so the old gate's `!== 0` check was redundant and slightly wrong.
 */
export const arePersonalDetailsValid = (
  values: CVBuilderFormValues
): boolean => {
  const pd = values.personalDetails;
  if (!pd) return false;

  const requiredFields: (keyof typeof pd)[] = [
    'firstname',
    'lastname',
    'email',
    'phone',
    'zip',
    'weeklyAvailableHours',
    'stipendBafoeg',
    'insuranceName',
    'insuranceType'
  ];
  const allRequiredFieldsFilled = requiredFields.every(field => {
    const value = pd[field];
    return value !== undefined && value !== '' && value !== null;
  });

  const formErrors = validateCVForm(values);
  const hasValidationErrors =
    !!formErrors.personalDetails &&
    Object.keys(formErrors.personalDetails).length > 0;

  return allRequiredFieldsFilled && !hasValidationErrors;
};
